<template>
  <div class="ai-audit-page">
    <div class="ai-container">
      <!-- 面包屑导航 -->
      <nav class="breadcrumb-nav">
        <router-link to="/">首页</router-link>
        <span class="separator">/</span>
        <router-link to="/product">产品中心</router-link>
        <span class="separator">/</span>
        <span class="current">AI审计</span>
      </nav>
    </div>

    <!-- 英雄区（产品Banner） -->
    <section class="ai-container" style="padding-top: 20px;">
      <div style="max-width: 1000px;">
        <h1>AI审计 · 大模型赋能审计新时代<br><span style="color: var(--ai-accent-blue);">自动阅读 · 智能分析 · 风险预测 · AI助手</span></h1>
        <p class="subhead">以大语言模型为核心引擎，打造"自动阅读 + 智能分析 + 风险预测 + AI助手"一体化智能审计平台，开启审计工作新模式。</p>
        <div style="display: flex; gap: 20px; flex-wrap: wrap;">
          <a href="#" class="btn btn-primary" @click.prevent="openModal">预约演示 <i class="fa-regular fa-calendar-check" style="margin-left: 8px;"></i></a>
          <a href="#" class="btn btn-outline" @click.prevent="downloadBrochure">下载产品手册 <i class="fa-regular fa-file-pdf" style="margin-left: 8px;"></i></a>
        </div>
      </div>

      <!-- 关键词云（SEO强化） -->
      <div class="keyword-cloud">
        <h2 class="keyword-tag highlight">AI审计</h2>
        <h2 class="keyword-tag">大模型审计</h2>
        <h2 class="keyword-tag">智能审计系统</h2>
        <h2 class="keyword-tag">合同智能审查</h2>
        <h2 class="keyword-tag">财务智能分析</h2>
        <h2 class="keyword-tag">风险预测</h2>
        <h2 class="keyword-tag">审计AI助手</h2>
        <h2 class="keyword-tag">生成式AI审计</h2>
        <h2 class="keyword-tag">OCR凭证识别</h2>
        <h2 class="keyword-tag">审计机器人</h2>
      </div>
    </section>

    <!-- 行业背景与痛点 -->
    <section class="ai-container">
      <h2><i class="fa-regular fa-star"></i> 行业背景与核心痛点</h2>
      <div class="card-grid-3">
        <div class="feature-card">
          <div class="card-icon"><i class="fa-regular fa-file-lines"></i></div>
          <h3>合同与凭证阅读耗时巨大</h3>
          <p>合同条款复杂，审计人员需逐条比对，人工阅读效率低，易遗漏关键风险点。</p>
        </div>
        <div class="feature-card">
          <div class="card-icon"><i class="fa-regular fa-chart-line"></i></div>
          <h3>财务报表分析依赖人工经验</h3>
          <p>关联指标多，需要跨期对比，风险识别依赖资深人员，新手难以胜任。</p>
        </div>
        <div class="feature-card">
          <div class="card-icon"><i class="fa-regular fa-clock"></i></div>
          <h3>风险预测能力不足</h3>
          <p>多数为事后发现，缺乏趋势预测，无法提前预警，风险管控滞后。</p>
        </div>
      </div>
      <p style="margin-top: 20px; font-size: 1.1rem;">在审计数字化、科技强审背景下，大语言模型（LLM）的成熟为审计工作从规则驱动向模型驱动转型提供了技术可能。</p>
    </section>

    <!-- 产品定位 -->
    <section class="ai-container">
      <div class="arch-card" style="background: var(--ai-primary-dark); color: white;">
        <h2 style="color: white; margin-bottom: 20px;">产品定位：企业级智能审计引擎</h2>
        <p style="font-size: 1.2rem;">AI审计平台以大语言模型为核心，整合审计知识库与数据分析能力，实现"自动读懂、自动分析、自动预警、自动辅助决策"，开启人机协同的智能审计新时代。</p>
      </div>
    </section>

    <!-- 核心功能模块（五个卡片，带颜色标识） -->
    <section class="ai-container">
      <h2><i class="fa-regular fa-cubes"></i> 五大核心能力</h2>
      <div class="card-grid-5">
        <!-- 模块1：合同智能阅读与条款分析 -->
        <div class="feature-card module-red">
          <div class="card-icon"><i class="fa-regular fa-file-signature"></i></div>
          <h3>合同智能阅读与条款分析</h3>
          <ul style="list-style: none; margin-top: 12px;">
            <li class="list-item"><i class="fa-regular fa-circle-check"></i> 自动识别关键条款</li>
            <li class="list-item"><i class="fa-regular fa-circle-check"></i> 抽取金额/期限/责任</li>
            <li class="list-item"><i class="fa-regular fa-circle-check"></i> 异常条款风险标记</li>
            <li class="list-item"><i class="fa-regular fa-circle-check"></i> 历史模板差异对比</li>
          </ul>
          <div style="margin-top: 16px;"><span class="keyword-tag">NLP语义理解</span><span class="keyword-tag">条款提取</span></div>
        </div>

        <!-- 模块2：凭证与附件自动识别 -->
        <div class="feature-card module-blue">
          <div class="card-icon"><i class="fa-regular fa-receipt"></i></div>
          <h3>凭证与附件自动识别</h3>
          <ul style="list-style: none; margin-top: 12px;">
            <li class="list-item"><i class="fa-regular fa-circle-check"></i> 自动读取财务凭证</li>
            <li class="list-item"><i class="fa-regular fa-circle-check"></i> OCR识别发票附件</li>
            <li class="list-item"><i class="fa-regular fa-circle-check"></i> 凭证合同自动匹配</li>
            <li class="list-item"><i class="fa-regular fa-circle-check"></i> 异常金额自动标记</li>
          </ul>
          <div style="margin-top: 16px;"><span class="keyword-tag">OCR识别</span><span class="keyword-tag">异常检测</span></div>
        </div>

        <!-- 模块3：财务报表智能分析 -->
        <div class="feature-card module-green">
          <div class="card-icon"><i class="fa-regular fa-chart-pie"></i></div>
          <h3>财务报表智能分析</h3>
          <ul style="list-style: none; margin-top: 12px;">
            <li class="list-item"><i class="fa-regular fa-circle-check"></i> 自动解析三张报表</li>
            <li class="list-item"><i class="fa-regular fa-circle-check"></i> 跨期趋势对比</li>
            <li class="list-item"><i class="fa-regular fa-circle-check"></i> 同行业对标分析</li>
            <li class="list-item"><i class="fa-regular fa-circle-check"></i> 异常波动自动提示</li>
          </ul>
          <div style="margin-top: 16px;"><span class="keyword-tag">财务指标</span><span class="keyword-tag">趋势分析</span></div>
        </div>

        <!-- 模块4：风险预测与预警模型 -->
        <div class="feature-card module-yellow">
          <div class="card-icon"><i class="fa-regular fa-bell"></i></div>
          <h3>风险预测与预警模型</h3>
          <ul style="list-style: none; margin-top: 12px;">
            <li class="list-item"><i class="fa-regular fa-circle-check"></i> 历史数据训练模型</li>
            <li class="list-item"><i class="fa-regular fa-circle-check"></i> 预测高风险领域</li>
            <li class="list-item"><i class="fa-regular fa-circle-check"></i> 风险概率评分</li>
            <li class="list-item"><i class="fa-regular fa-circle-check"></i> 实时预警推送</li>
          </ul>
          <div style="margin-top: 16px;"><span class="keyword-tag">机器学习</span><span class="keyword-tag">风险评分</span></div>
        </div>

        <!-- 模块5：审计AI助手（Copilot模式） -->
        <div class="feature-card module-purple">
          <div class="card-icon"><i class="fa-regular fa-robot"></i></div>
          <h3>审计AI助手 (Copilot)</h3>
          <ul style="list-style: none; margin-top: 12px;">
            <li class="list-item"><i class="fa-regular fa-circle-check"></i> 实时回答审计问题</li>
            <li class="list-item"><i class="fa-regular fa-circle-check"></i> 自动生成底稿初稿</li>
            <li class="list-item"><i class="fa-regular fa-circle-check"></i> 自动生成报告摘要</li>
            <li class="list-item"><i class="fa-regular fa-circle-check"></i> 法规引用建议</li>
          </ul>
          <div style="margin-top: 16px;"><span class="keyword-tag">LLM</span><span class="keyword-tag">知识问答</span></div>
        </div>
      </div>
    </section>

    <!-- 技术架构亮点 -->
    <section class="ai-container">
      <h2><i class="fa-regular fa-microchip"></i> 技术架构亮点</h2>
      <div class="arch-card">
        <div style="display: flex; flex-wrap: wrap; gap: 20px; align-items: center; justify-content: center; margin-bottom: 30px;">
          <span class="compliance-badge">大语言模型引擎</span>
          <span class="compliance-badge">私有化部署</span>
          <span class="compliance-badge">RAG知识增强</span>
          <span class="compliance-badge">数据安全控制</span>
          <span class="compliance-badge">审计数据集市对接</span>
          <span class="compliance-badge">风险矩阵融合</span>
        </div>
        <div class="arch-grid">
          <div class="arch-item">
            <i class="fa-regular fa-brain" style="font-size: 2rem; color: var(--ai-primary-dark); margin-bottom: 16px; display: block;"></i>
            <h4>企业级大模型</h4>
            <p>支持主流开源/商用LLM，可根据企业需求选择，提供模型微调与优化服务。</p>
          </div>
          <div class="arch-item">
            <i class="fa-regular fa-database" style="font-size: 2rem; color: var(--ai-primary-dark); margin-bottom: 16px; display: block;"></i>
            <h4>RAG知识增强</h4>
            <p>融合企业审计知识库、法规库、历史底稿，提升模型回答准确性与专业性。</p>
          </div>
          <div class="arch-item">
            <i class="fa-regular fa-lock" style="font-size: 2rem; color: var(--ai-primary-dark); margin-bottom: 16px; display: block;"></i>
            <h4>私有化安全架构</h4>
            <p>支持全栈私有化部署，数据不出域，满足央企国企数据安全合规要求。</p>
          </div>
        </div>
      </div>
    </section>

    <!-- 典型应用场景 -->
    <section class="ai-container">
      <h2><i class="fa-regular fa-building"></i> 典型应用场景</h2>
      <div class="card-grid-4">
        <div class="feature-card">
          <i class="fa-regular fa-file-contract card-icon"></i>
          <h3>合同专项审计</h3>
          <p>自动扫描数千份合同，标记高风险条款，生成合同风险报告，审计效率提升70%。</p>
        </div>
        <div class="feature-card">
          <i class="fa-regular fa-chart-simple card-icon"></i>
          <h3>财务专项审计</h3>
          <p>自动识别异常科目，生成分析说明，提供审计关注点，辅助财务尽调。</p>
        </div>
        <div class="feature-card">
          <i class="fa-regular fa-gauge-high card-icon"></i>
          <h3>风险前置预警</h3>
          <p>提前识别高风险业务，风险评分排序，推送决策建议，实现风险事前防控。</p>
        </div>
        <div class="feature-card">
          <i class="fa-regular fa-comment card-icon"></i>
          <h3>审计人员智能辅助</h3>
          <p>审计报告自动起草、底稿自动生成、法规自动引用，降低重复劳动。</p>
        </div>
      </div>
    </section>

    <!-- 产品核心优势 -->
    <section class="ai-container">
      <h2><i class="fa-regular fa-star"></i> 产品核心优势</h2>
      <div class="card-grid-4">
        <div class="feature-card">
          <i class="fa-regular fa-rocket card-icon" style="color: var(--ai-accent-blue);"></i>
          <h3>效率提升 50%-70%</h3>
          <p>自动化替代人工阅读与分析，大幅缩短审计周期。</p>
        </div>
        <div class="feature-card">
          <i class="fa-regular fa-scale-balanced card-icon" style="color: var(--ai-accent-blue);"></i>
          <h3>降低人为偏差</h3>
          <p>减少经验依赖，统一审计标准，提升评价一致性。</p>
        </div>
        <div class="feature-card">
          <i class="fa-regular fa-bullseye card-icon" style="color: var(--ai-accent-blue);"></i>
          <h3>风险识别更精准</h3>
          <p>基于历史数据与模型学习，发现隐性风险，提高预警准确率。</p>
        </div>
        <div class="feature-card">
          <i class="fa-regular fa-chart-line card-icon" style="color: var(--ai-accent-blue);"></i>
          <h3>支撑智能审计升级</h3>
          <p>推动企业从规则型审计向AI驱动型审计转型。</p>
        </div>
      </div>
    </section>

    <!-- 项目成果与价值（数据指标） -->
    <section class="ai-container">
      <h2><i class="fa-regular fa-chart-simple"></i> 关键成果与价值</h2>
      <div class="stat-grid">
        <div class="stat-card">
          <div class="stat-number">50%-70%</div>
          <p>审计效率提升</p>
        </div>
        <div class="stat-card">
          <div class="stat-number">秒级</div>
          <p>合同智能分析</p>
        </div>
        <div class="stat-card">
          <div class="stat-number">实时</div>
          <p>风险预警</p>
        </div>
        <div class="stat-card">
          <div class="stat-number">7×24</div>
          <p>AI助手在线</p>
        </div>
      </div>
      <div style="margin-top: 40px;">
        <ul style="list-style: none; display: flex; flex-wrap: wrap; gap: 30px; justify-content: center;">
          <li class="list-item"><i class="fa-regular fa-circle-check"></i> 合同阅读时间从天级缩短到分钟级</li>
          <li class="list-item"><i class="fa-regular fa-circle-check"></i> 财务报表异常识别准确率提升60%</li>
          <li class="list-item"><i class="fa-regular fa-circle-check"></i> 风险预测提前3-6个月</li>
          <li class="list-item"><i class="fa-regular fa-circle-check"></i> 审计报告起草时间减少80%</li>
        </ul>
      </div>
    </section>

    <!-- 适用客户类型 -->
    <section class="ai-container">
      <h2><i class="fa-regular fa-users"></i> 适用客户群体</h2>
      <div class="client-tags">
        <span class="client-tag">央企集团</span>
        <span class="client-tag">地方国企</span>
        <span class="client-tag">金融机构</span>
        <span class="client-tag">大型制造企业</span>
        <span class="client-tag">审计机关</span>
        <span class="client-tag">会计师事务所</span>
        <span class="client-tag">内审部门</span>
        <span class="client-tag">风控合规部门</span>
      </div>
    </section>

    <!-- 常见问题 FAQ -->
    <section class="ai-container">
      <h2 style="text-align: center;"><i class="fa-regular fa-circle-question"></i> 常见问题</h2>
      <div class="faq-list">
        <div class="faq-item">
          <div class="faq-question"><i class="fa-regular fa-message"></i> 大模型审计是否涉及数据安全风险？</div>
          <div class="faq-answer">平台支持私有化部署，所有数据在企业内部闭环，不对外传输，符合等保2.0及国资监管要求。模型也可选择国产化信创适配。</div>
        </div>
        <div class="faq-item">
          <div class="faq-question"><i class="fa-regular fa-message"></i> 需要多少训练数据？模型如何适配企业特点？</div>
          <div class="faq-answer">支持零样本启动，通用审计任务可直接使用；针对企业特殊业务，可通过少量样本微调（few-shot）或RAG知识库增强，快速适配。</div>
        </div>
        <div class="faq-item">
          <div class="faq-question"><i class="fa-regular fa-message"></i> 能否与现有审计系统集成？</div>
          <div class="faq-answer">提供标准化API接口，可无缝对接古杉非现场审计平台、审计数据集市、风险矩阵系统，实现数据互通与能力增强。</div>
        </div>
        <div class="faq-item">
          <div class="faq-question"><i class="fa-regular fa-message"></i> 实施周期需要多久？</div>
          <div class="faq-answer">一般2-3个月完成试点上线，包括模型部署、知识库构建、人员培训和场景验证。</div>
        </div>
        <div class="faq-item">
          <div class="faq-question"><i class="fa-regular fa-message"></i> AI助手支持哪些语言？是否支持审计专业术语？</div>
          <div class="faq-answer">支持中英文双语，内置审计专业词典和法规库，可准确理解审计领域复杂问题。</div>
        </div>
      </div>
    </section>

    <!-- 最终CTA -->
    <section class="ai-container" style="padding-bottom: 80px;">
      <div class="cta-block">
        <h2 style="font-size: 2.4rem;">开启大模型赋能审计新时代</h2>
        <p style="font-size: 1.2rem; margin: 16px 0 40px; opacity: 0.9;">预约专家演示，了解如何通过AI审计实现效率飞跃与风险智能预警。</p>
        <div style="display: flex; gap: 20px; justify-content: center; flex-wrap: wrap;">
          <a href="#" class="btn btn-primary" @click.prevent="openModal" style="background: white; color: var(--ai-primary-dark);">预约免费演示</a>
          <a href="tel:4008881234" class="btn btn-outline" style="border-color: white; color: white;">致电 400-888-1234</a>
        </div>
        <p style="margin-top: 32px; font-size: 1rem;"><i class="fa-regular fa-envelope"></i> 发送需求至 ai-audit@gushandata.cn</p>
      </div>
    </section>


        <!-- 弹窗留资 -->
    <div class="modal-overlay" v-if="showModal" @click.self="showModal = false">
      <div class="modal-container">
        <span class="modal-close" @click="showModal = false">&times;</span>
        <h3 style="margin-bottom: 20px;">预约咨询</h3>
        <form @submit.prevent="submitForm">
          <div style="margin-bottom: 15px;">
            <input type="text" placeholder="姓名 *" style="width:100%; padding:12px; border-radius:8px; border:1px solid #ddd;">
          </div>
          <div style="margin-bottom: 15px;">
            <input type="text" placeholder="公司名称" style="width:100%; padding:12px; border-radius:8px; border:1px solid #ddd;">
          </div>
          <div style="margin-bottom: 15px;">
            <input type="text" placeholder="职位" style="width:100%; padding:12px; border-radius:8px; border:1px solid #ddd;">
          </div>
          <div style="margin-bottom: 15px;">
            <input type="tel" placeholder="联系电话 *" style="width:100%; padding:12px; border-radius:8px; border:1px solid #ddd;">
          </div>
          <div style="margin-bottom: 15px;">
            <select style="width:100%; padding:12px; border-radius:8px;">
              <option>数据治理咨询方向</option>
              <option>顶层设计</option>
              <option>数据标准</option>
              <option>主数据</option>
              <option>数据质量</option>
            </select>
          </div>
          <button class="btn-primary" style="width:100%;">提交预约</button>
        </form>
      </div>
    </div>

    <!-- 扫码添加顾问：二维码弹窗（预留2个二维码） -->
    <div class="modal-overlay" v-if="showQrModal" @click.self="showQrModal = false">
      <div class="modal-container qr-modal">
        <span class="modal-close" @click="showQrModal = false">&times;</span>
        <h3 style="margin-bottom: 12px;">添加顾问（扫码）</h3>
        <div class="qr-grid">
          <div class="qr-item" v-for="(img, idx) in qrImages" :key="idx">
            <img v-if="img" :src="img" alt="QR code" class="qr-img" />
            <div v-else class="qr-placeholder">二维码 {{ idx + 1 }}（占位）</div>
            <div class="qr-caption">扫码添加顾问 {{ idx + 1 }}</div>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
export default {
  name: 'AiAuditDetail',
  data() {
    return {
      showModal: false,
      form: {
        name: '',
        company: '',
        email: '',
        phone: '',
        message: ''
      }
    };
  },
  mounted() {
    window.scrollTo(0, 0);
  },
  methods: {
    openModal() {
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
    },
    submitDemo() {
      alert('感谢您的预约，古杉数据专家将在24小时内与您联系。');
      this.showModal = false;
      this.form = { name: '', company: '', email: '', phone: '', message: '' };
    },
    downloadBrochure() {
      alert('产品手册下载中…… (实际可替换为PDF链接)');
    }
  }
};
</script>

<style scoped>
.ai-audit-page {
  --ai-primary-dark: #1D4E4E;
  --ai-primary-soft: #e9f3f2;
  --ai-accent-gold: #D4AF37;
  --ai-accent-blue: #2A7DE1;
  --ai-gray-light: #f8fafc;
  --ai-gray-border: #e2e8f0;
  --ai-text-dark: #0f2a2a;
  --ai-text-regular: #2d3f4b;
  --ai-white: #ffffff;
  --ai-shadow: 0 12px 30px rgba(0, 40, 40, 0.06);

  background-color: #ffffff;
  color: var(--ai-text-regular);
  line-height: 1.5;
  font-family: 'Inter', 'PingFang SC', 'Roboto', system-ui, -apple-system, BlinkMacSystemFont, sans-serif;
}

/* 面包屑导航 */
.breadcrumb-nav {
  padding: 20px 0;
  font-size: 0.9rem;
}

.breadcrumb-nav a {
  color: #6b7280;
  text-decoration: none;
  transition: color 0.2s;
}

.breadcrumb-nav a:hover {
  color: var(--ai-primary-dark);
}

.breadcrumb-nav .separator {
  margin: 0 8px;
  color: #9ca3af;
}

.breadcrumb-nav .current {
  color: var(--ai-primary-dark);
  font-weight: 600;
}

.ai-container {
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 24px;
}

section.ai-container {
  padding: 64px 24px;
}

.ai-audit-page h1,
.ai-audit-page h2,
.ai-audit-page h3 {
  font-weight: 600;
  letter-spacing: -0.02em;
  color: var(--ai-text-dark);
}

.ai-audit-page h1 {
  font-size: 3.2rem;
  line-height: 1.2;
}

.ai-audit-page h2 {
  font-size: 2.2rem;
  margin-bottom: 24px;
}

.ai-audit-page h2 i {
  color: var(--ai-accent-blue);
  margin-right: 8px;
}

.ai-audit-page h3 {
  font-size: 1.4rem;
  margin-bottom: 12px;
}

.subhead {
  font-size: 1.2rem;
  color: #4f6f7f;
  max-width: 800px;
  margin-bottom: 40px;
}

.btn {
  display: inline-block;
  padding: 12px 30px;
  border-radius: 40px;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.2s;
  font-size: 1rem;
  border: 1px solid transparent;
  cursor: pointer;
}

.btn-primary {
  background-color: var(--ai-primary-dark);
  color: white;
  box-shadow: 0 4px 12px rgba(29, 78, 78, 0.25);
}

.btn-primary:hover {
  background-color: #0e3a3a;
  transform: translateY(-2px);
}

.btn-outline {
  background-color: transparent;
  border: 1.5px solid var(--ai-primary-dark);
  color: var(--ai-primary-dark);
}

.btn-outline:hover {
  background-color: var(--ai-primary-dark);
  color: white;
}

.btn-accent {
  background-color: var(--ai-accent-blue);
  color: white;
  box-shadow: 0 4px 12px rgba(42, 125, 225, 0.3);
}

.btn-accent:hover {
  background-color: #1b64c5;
  transform: translateY(-2px);
}

/* 关键词标签云 */
.keyword-cloud {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin: 30px 0 20px;
}

.keyword-tag {
  background: var(--ai-primary-soft);
  color: var(--ai-primary-dark);
  padding: 6px 16px;
  border-radius: 40px;
  font-size: 0.85rem;
  font-weight: 500;
  border: 1px solid transparent;
}

.keyword-tag.highlight {
  background: var(--ai-accent-gold);
  color: #1e3c3c;
}

/* 卡片网格 */
.card-grid-3, .card-grid-4, .card-grid-5 {
  display: grid;
  gap: 24px;
  margin: 40px 0;
}

.card-grid-3 {
  grid-template-columns: repeat(3, 1fr);
}

.card-grid-4 {
  grid-template-columns: repeat(4, 1fr);
}

.card-grid-5 {
  grid-template-columns: repeat(5, 1fr);
}

.feature-card {
  background: white;
  border-radius: 24px;
  padding: 28px 24px;
  box-shadow: var(--ai-shadow);
  border: 1px solid var(--ai-gray-border);
  transition: transform 0.2s;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.feature-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 24px 40px rgba(29, 78, 78, 0.1);
}

.card-icon {
  font-size: 2.5rem;
  color: var(--ai-primary-dark);
  margin-bottom: 20px;
}

.list-item {
  display: flex;
  gap: 12px;
  margin-bottom: 12px;
  align-items: flex-start;
}

.list-item i {
  color: var(--ai-accent-blue);
  font-size: 1.1rem;
  margin-top: 3px;
  min-width: 20px;
}

.stat-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
  margin-top: 40px;
}

.stat-card {
  background: var(--ai-primary-soft);
  border-radius: 32px;
  padding: 32px 24px;
  text-align: center;
}

.stat-number {
  font-size: 2.8rem;
  font-weight: 700;
  color: var(--ai-primary-dark);
  margin-bottom: 8px;
}

.client-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 24px;
}

.client-tag {
  background: white;
  border: 1px solid var(--ai-gray-border);
  padding: 8px 22px;
  border-radius: 40px;
  font-weight: 500;
  color: var(--ai-text-dark);
}

.case-card {
  background: white;
  border-radius: 24px;
  padding: 28px;
  box-shadow: var(--ai-shadow);
}

.case-stats {
  font-size: 1.8rem;
  font-weight: 700;
  color: var(--ai-primary-dark);
  margin: 16px 0 8px;
}

/* 技术架构卡片 */
.arch-card {
  background: var(--ai-primary-soft);
  border-radius: 32px;
  padding: 36px;
  margin-top: 30px;
}

.arch-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  margin-top: 24px;
}

.arch-item {
  background: white;
  border-radius: 24px;
  padding: 24px;
}

/* 合规认证 */
.compliance-badge {
  display: inline-block;
  background: var(--ai-accent-gold);
  color: #1e3c3c;
  padding: 8px 18px;
  border-radius: 40px;
  font-weight: 600;
  margin-right: 12px;
  margin-bottom: 12px;
}

/* FAQ */
.faq-list {
  max-width: 900px;
  margin: 40px auto 0;
}

.faq-item {
  padding: 22px 0;
  border-bottom: 1px solid var(--ai-gray-border);
}

.faq-question {
  font-weight: 700;
  font-size: 1.2rem;
  display: flex;
  gap: 16px;
  color: var(--ai-text-dark);
}

.faq-question i {
  color: var(--ai-accent-gold);
}

.faq-answer {
  margin-top: 12px;
  color: #3f5465;
  padding-left: 40px;
}

.cta-block {
  background: var(--ai-primary-dark);
  border-radius: 56px;
  padding: 64px 56px;
  color: white;
  text-align: center;
}

.cta-block h2 {
  color: white;
}

.cta-block .btn-primary {
  background-color: white;
  color: var(--ai-primary-dark);
  margin: 0 8px;
}

.cta-block .btn-outline {
  border-color: white;
  color: white;
}

.cta-block .btn-outline:hover {
  background-color: white;
  color: var(--ai-primary-dark);
}

/* 模态框 */
.modal {
  display: flex;
  position: fixed;
  z-index: 1000;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0,0,0,0.5);
  align-items: center;
  justify-content: center;
}

.modal-content {
  background: white;
  border-radius: 40px;
  max-width: 500px;
  width: 90%;
  padding: 40px;
  box-shadow: 0 30px 60px rgba(0,0,0,0.3);
  position: relative;
}

.close-modal {
  position: absolute;
  top: 20px;
  right: 24px;
  font-size: 28px;
  cursor: pointer;
  color: #999;
}

.modal h3 {
  margin-bottom: 24px;
}

.modal .form-group {
  margin-bottom: 20px;
}

.modal input, .modal textarea {
  width: 100%;
  padding: 12px 18px;
  border: 1px solid var(--ai-gray-border);
  border-radius: 30px;
  font-size: 1rem;
  outline: none;
}

.modal textarea {
  border-radius: 20px;
}

.modal .btn {
  width: 100%;
  margin-top: 10px;
}

/* 响应式 */
@media (max-width: 1100px) {
  .card-grid-5 {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 900px) {
  .card-grid-4, .card-grid-5, .stat-grid, .arch-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  .card-grid-3 {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 700px) {
  .card-grid-4, .card-grid-5, .stat-grid, .arch-grid {
    grid-template-columns: 1fr;
  }
  .ai-audit-page h1 { font-size: 2.5rem; }
}

/* 模块颜色标识 */
.module-red { border-top: 5px solid #e53e3e; }
.module-blue { border-top: 5px solid #3182ce; }
.module-green { border-top: 5px solid #38a169; }
.module-yellow { border-top: 5px solid #d69e2e; }
.module-purple { border-top: 5px solid #9f7aea; }
</style>
